/**
 * Roles y permisos del policlínico.
 *
 * Los 5 roles que nacen con el tenant (admin_tenant, medico, recepcion, caja,
 * contabilidad) son un punto de partida, no una camisa de fuerza: acá se
 * editan y se crean los que haga falta.
 *
 * Para el caso "recepción que también cobra" NO hace falta un rol nuevo: en
 * Personal se le marcan los dos roles a la misma cuenta y los permisos se
 * suman. Esta pantalla es para cuando ninguna combinación existente sirve.
 *
 * Requiere rol:read para entrar; rol:create / rol:update / rol:delete para
 * cada acción.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, ShieldCheck, Trash2 } from "lucide-react";
import {
  type Permiso,
  type Rol,
  createRol,
  deleteRol,
  listPermisos,
  listRoles,
  listUsuarios,
  updateRol,
} from "../api/users";
import { formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useBase } from "../auth/TenantBrandingProvider";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

/** Nombre legible de cada categoría del catálogo de permisos. */
const CATEGORIAS: Record<string, string> = {
  tenancy: "Policlínico, sedes y especialidades",
  identity: "Usuarios, roles y accesos",
  clinical: "Pacientes e historia clínica",
  profesionales: "Profesionales y agendas",
  facturacion: "Facturación y caja",
  compliance: "Auditoría y cumplimiento",
  general: "General",
};

function tituloCategoria(c: string): string {
  return CATEGORIAS[c] ?? c.charAt(0).toUpperCase() + c.slice(1);
}

export function RolesPage() {
  const { me } = useAuth();
  const base = useBase();
  const queryClient = useQueryClient();
  const [seleccionado, setSeleccionado] = useState<number | null>(null);
  const [crearOpen, setCrearOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const puedeCrear = me?.is_super_admin || (me?.permisos.includes("rol:create") ?? false);
  const puedeEditar = me?.is_super_admin || (me?.permisos.includes("rol:update") ?? false);
  const puedeBorrar = me?.is_super_admin || (me?.permisos.includes("rol:delete") ?? false);

  const { data: roles = [], isLoading } = useQuery({ queryKey: ["roles"], queryFn: listRoles });
  const { data: permisos = [] } = useQuery({
    queryKey: ["permisos"],
    queryFn: listPermisos,
    staleTime: 10 * 60_000,
  });
  // Para avisar cuántas cuentas usa cada rol antes de tocarlo.
  const { data: usuarios = [] } = useQuery({
    queryKey: ["usuarios"],
    // Envuelta y no pasada directa: react-query llama a `queryFn` con su
    // contexto, que aquí caería en el `opts` de `listUsuarios`. Funcionaba de
    // milagro —`contexto.soloPersonal` es undefined— pero `tsc -b` no pasaba,
    // y eso es el build de producción entero.
    queryFn: () => listUsuarios(),
    staleTime: 60_000,
  });

  const cuentasPorRol = useMemo(() => {
    const m = new Map<number, number>();
    for (const u of usuarios) {
      for (const rid of u.rol_ids) m.set(rid, (m.get(rid) ?? 0) + 1);
    }
    return m;
  }, [usuarios]);

  // Primer rol seleccionado por defecto, para no abrir en una pantalla vacía.
  useEffect(() => {
    if (seleccionado == null && roles.length > 0) setSeleccionado(roles[0].id);
  }, [roles, seleccionado]);

  const rol = roles.find((r) => r.id === seleccionado) ?? null;

  const borrar = useMutation({
    mutationFn: deleteRol,
    onSuccess: (_d, rolId) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      if (seleccionado === rolId) setSeleccionado(null);
      setError(null);
    },
    onError: (e) => setError(formatApiError(e, "No se pudo eliminar el rol.")),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Roles y permisos</h1>
          {/* El aviso lleva AL SITIO. Decir "márcale ambos roles en Personal"
              y no poner el enlace obliga a buscar en el menú una pantalla que
              se llama distinto de lo que uno está haciendo. */}
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Qué puede hacer cada rol. Una cuenta puede tener{" "}
            <span className="font-medium text-slate-700">varios roles a la vez</span> y sus
            permisos se suman: quien hace recepción y caja, o el médico que además es
            socio, lleva los dos roles en vez de uno nuevo a medida.
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Los roles se asignan a cada persona en{" "}
            <Link
              to={`${base}/personal`}
              className="font-medium text-[color:var(--color-link)] hover:underline"
            >
              Personal
            </Link>
            , marcando todos los que le correspondan.
          </p>
        </div>
        {puedeCrear && (
          <Button onClick={() => setCrearOpen(true)}>
            <Plus className="h-4 w-4" />
            Nuevo rol
          </Button>
        )}
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
        {/* Lista de roles */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-400" aria-hidden="true" />
              <span className="text-sm font-medium text-slate-700">Roles</span>
              <span className="ml-auto text-xs text-slate-500">{roles.length}</span>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading && (
              <div className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</div>
            )}
            <ul className="divide-y divide-slate-100">
              {roles.map((r) => {
                const cuentas = cuentasPorRol.get(r.id) ?? 0;
                const activo = r.id === seleccionado;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => setSeleccionado(r.id)}
                      aria-current={activo ? "true" : undefined}
                      className={`flex w-full items-center gap-2 px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[color:var(--color-link)] ${
                        activo ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-900">
                          {r.nombre}
                        </div>
                        <div className="truncate font-mono text-xs text-slate-400">
                          {r.codigo}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs text-slate-500">
                        {cuentas} {cuentas === 1 ? "cuenta" : "cuentas"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>

        {/* Matriz de permisos del rol elegido */}
        {rol ? (
          <MatrizPermisos
            key={rol.id}
            rol={rol}
            permisos={permisos}
            cuentas={cuentasPorRol.get(rol.id) ?? 0}
            puedeEditar={puedeEditar}
            puedeBorrar={puedeBorrar}
            onBorrar={() => borrar.mutate(rol.id)}
            borrando={borrar.isPending}
            onError={setError}
          />
        ) : (
          <Card>
            <CardBody>
              <p className="py-12 text-center text-sm text-slate-500">
                Elige un rol de la izquierda para ver y editar sus permisos.
              </p>
            </CardBody>
          </Card>
        )}
      </div>

      <CrearRolDialog
        open={crearOpen}
        onClose={() => setCrearOpen(false)}
        permisos={permisos}
        roles={roles}
        onCreado={(nuevo) => setSeleccionado(nuevo.id)}
      />
    </div>
  );
}

function MatrizPermisos({
  rol,
  permisos,
  cuentas,
  puedeEditar,
  puedeBorrar,
  onBorrar,
  borrando,
  onError,
}: {
  rol: Rol;
  permisos: Permiso[];
  cuentas: number;
  puedeEditar: boolean;
  puedeBorrar: boolean;
  onBorrar: () => void;
  borrando: boolean;
  onError: (m: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [seleccion, setSeleccion] = useState<Set<string>>(
    () => new Set(rol.permisos.map((p) => p.codigo)),
  );
  const [guardado, setGuardado] = useState(false);

  const original = useMemo(
    () => new Set(rol.permisos.map((p) => p.codigo)),
    [rol.permisos],
  );
  const sucio =
    seleccion.size !== original.size || [...seleccion].some((c) => !original.has(c));

  const porCategoria = useMemo(() => {
    const m = new Map<string, Permiso[]>();
    for (const p of [...permisos].sort((a, b) => a.codigo.localeCompare(b.codigo))) {
      if (!m.has(p.categoria)) m.set(p.categoria, []);
      m.get(p.categoria)!.push(p);
    }
    return [...m.entries()].sort((a, b) =>
      tituloCategoria(a[0]).localeCompare(tituloCategoria(b[0])),
    );
  }, [permisos]);

  const guardar = useMutation({
    mutationFn: () => updateRol(rol.id, { permiso_codigos: [...seleccion] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      // El usuario puede estar editando su propio rol: refrescar su sesión.
      queryClient.invalidateQueries({ queryKey: ["me"] });
      onError(null);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2000);
    },
    onError: (e) => onError(formatApiError(e, "No se pudieron guardar los permisos.")),
  });

  function alternar(codigo: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  }

  function alternarCategoria(items: Permiso[]) {
    const todos = items.every((p) => seleccion.has(p.codigo));
    setSeleccion((prev) => {
      const next = new Set(prev);
      for (const p of items) {
        if (todos) next.delete(p.codigo);
        else next.add(p.codigo);
      }
      return next;
    });
  }

  const bloqueado = rol.es_sistema || !puedeEditar;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <KeyRound className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
              {rol.nombre}
              {rol.es_sistema && <Badge>del sistema</Badge>}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              <span className="font-mono">{rol.codigo}</span> · {seleccion.size} permisos ·{" "}
              {cuentas} {cuentas === 1 ? "cuenta" : "cuentas"}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {puedeBorrar && !rol.es_sistema && (
              <Button variant="ghost" onClick={onBorrar} disabled={borrando}>
                <Trash2 className="h-4 w-4" />
                Eliminar
              </Button>
            )}
            {!bloqueado && (
              <Button onClick={() => guardar.mutate()} disabled={!sucio || guardar.isPending}>
                {guardado ? "Guardado" : "Guardar permisos"}
              </Button>
            )}
          </div>
        </div>

        {rol.es_sistema && (
          <p className="mt-2 text-xs text-slate-500">
            Este rol es de la plataforma y no se puede modificar.
          </p>
        )}
        {cuentas > 0 && !bloqueado && (
          <p className="mt-2 text-xs text-amber-700">
            Afecta a {cuentas} {cuentas === 1 ? "cuenta" : "cuentas"} en cuanto guardes.
          </p>
        )}
      </CardHeader>

      <CardBody className="space-y-5">
        {porCategoria.map(([cat, items]) => {
          const marcados = items.filter((p) => seleccion.has(p.codigo)).length;
          return (
            <fieldset key={cat}>
              <legend className="mb-2 flex w-full items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {tituloCategoria(cat)}
                </span>
                <span className="text-xs text-slate-400">
                  {marcados}/{items.length}
                </span>
                {!bloqueado && (
                  <button
                    type="button"
                    onClick={() => alternarCategoria(items)}
                    className="ml-auto text-xs font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
                  >
                    {marcados === items.length ? "Ninguno" : "Todos"}
                  </button>
                )}
              </legend>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                {items.map((p) => (
                  <label
                    key={p.codigo}
                    className={`flex items-start gap-2 rounded-lg p-2 text-sm ${
                      bloqueado ? "opacity-60" : "cursor-pointer hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={seleccion.has(p.codigo)}
                      disabled={bloqueado}
                      onChange={() => alternar(p.codigo)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
                    />
                    <span className="min-w-0">
                      <span className="block text-slate-700">{p.descripcion}</span>
                      <span className="block font-mono text-xs text-slate-400">{p.codigo}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </CardBody>
    </Card>
  );
}

function CrearRolDialog({
  open,
  onClose,
  permisos,
  roles,
  onCreado,
}: {
  open: boolean;
  onClose: () => void;
  permisos: Permiso[];
  roles: Rol[];
  onCreado: (r: Rol) => void;
}) {
  const queryClient = useQueryClient();
  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState("");
  const [copiarDe, setCopiarDe] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setNombre("");
      setCodigo("");
      setCopiarDe(null);
      setError(null);
    }
  }, [open]);

  // El código se deriva del nombre, pero se puede corregir a mano.
  const [codigoTocado, setCodigoTocado] = useState(false);
  const codigoFinal = codigoTocado
    ? codigo
    : nombre
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 50);

  const crear = useMutation({
    mutationFn: () => {
      const base = roles.find((r) => r.id === copiarDe);
      return createRol({
        codigo: codigoFinal,
        nombre: nombre.trim(),
        // Copiar de un rol existente es el atajo real: casi siempre se quiere
        // "lo mismo que recepción, más algo".
        permiso_codigos: base ? base.permisos.map((p) => p.codigo) : [],
      });
    },
    onSuccess: (nuevo) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      onCreado(nuevo);
      onClose();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo crear el rol.")),
  });

  const valido = nombre.trim().length >= 2 && /^[a-z0-9_]{2,50}$/.test(codigoFinal);

  return (
    <Dialog open={open} onClose={onClose} title="Nuevo rol">
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field label="Nombre" htmlFor="rol-nombre">
          <Input
            id="rol-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Asistente de consultorio"
          />
        </Field>

        <Field
          label="Código"
          htmlFor="rol-codigo"
          hint="Identificador estable. Minúsculas, números y guion bajo."
        >
          <Input
            id="rol-codigo"
            value={codigoFinal}
            onChange={(e) => {
              setCodigoTocado(true);
              setCodigo(e.target.value);
            }}
            className="font-mono"
            placeholder="asistente_consultorio"
          />
        </Field>

        <Field
          label="Partir de"
          htmlFor="rol-base"
          hint="Copia los permisos de un rol existente. Después los ajustas."
        >
          <select
            id="rol-base"
            value={copiarDe ?? ""}
            onChange={(e) => setCopiarDe(e.target.value ? Number(e.target.value) : null)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
          >
            <option value="">Sin permisos (empezar de cero)</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nombre} ({r.permisos.length} permisos)
              </option>
            ))}
          </select>
        </Field>

        <p className="text-xs text-slate-500">
          {permisos.length} permisos disponibles para ajustar después de crearlo.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={!valido || crear.isPending}>
            Crear rol
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
